/**
 * src/components/admin/SystemHealthPanel.js
 *
 * Compact system health widget for AdminDashboard.
 * Shows feedback stats, support ticket status, and platform health indicators.
 * Pulls from feedbackService + adminService — works on Spark plan.
 */

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  HeartPulse,
  MessageSquareText,
  TicketCheck,
  Star,
  Bug,
  TrendingUp,
  ArrowUpRight,
  CheckCircle,
  Clock,
  AlertTriangle,
  Loader,
} from "lucide-react";
import * as feedbackService from "../../services/feedbackService";
import { errorDev } from '../../utils/logger.js';

function HealthIndicator({ label, value, status, Icon }) {
  const colors = {
    good: "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400",
    warning: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400",
    critical: "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400",
  };

  const StatusIcon = {
    good: CheckCircle,
    warning: Clock,
    critical: AlertTriangle,
  }[status] || CheckCircle;

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2.5">
        <div className={`w-7 h-7 rounded-lg ${colors[status]} flex items-center justify-center`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-bold text-gray-900 dark:text-white">
          {value}
        </span>
        <StatusIcon className={`w-3.5 h-3.5 ${status === "good" ? "text-green-500" : status === "warning" ? "text-amber-500" : "text-red-500"}`} />
      </div>
    </div>
  );
}

export default function SystemHealthPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      const data = await feedbackService.getFeedbackStats();
      setStats(data);
    } catch (err) {
      // Silent fail
    errorDev("Operation failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
        <div className="flex items-center justify-center py-6">
          <span className="orrith-loader orrith-loader-sm orrith-loader-primary" />
        </div>
      </div>
    );
  }

  const feedbackStatus =
    (stats?.new || 0) > 10 ? "critical" : (stats?.new || 0) > 3 ? "warning" : "good";
  const bugStatus =
    (stats?.bugReports || 0) > 5 ? "critical" : (stats?.bugReports || 0) > 0 ? "warning" : "good";
  const ratingStatus =
    (stats?.avgRating || 5) < 2.5 ? "critical" : (stats?.avgRating || 5) < 3.5 ? "warning" : "good";

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <HeartPulse className="w-4 h-4 text-green-600 dark:text-green-400" />
          System Health
        </h2>
        <Link
          to="/admin/analytics"
          className="text-xs text-green-600 dark:text-green-400 hover:underline flex items-center gap-0.5"
        >
          Details <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-gray-50 dark:bg-neutral-900 rounded-lg px-3 py-2 text-center">
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {stats?.total ?? 0}
          </p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Feedback</p>
        </div>
        <div className="bg-gray-50 dark:bg-neutral-900 rounded-lg px-3 py-2 text-center">
          <p className="text-lg font-bold text-gray-900 dark:text-white flex items-center justify-center gap-0.5">
            {stats?.avgRating ?? "—"}
            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
          </p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Avg Rating</p>
        </div>
        <div className="bg-gray-50 dark:bg-neutral-900 rounded-lg px-3 py-2 text-center">
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {stats?.new ?? 0}
          </p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">Unreviewed</p>
        </div>
      </div>

      {/* Health Indicators */}
      <div className="divide-y divide-gray-100 dark:divide-neutral-700">
        <HealthIndicator
          label="Pending Feedback"
          value={stats?.new ?? 0}
          status={feedbackStatus}
          Icon={MessageSquareText}
        />
        <HealthIndicator
          label="Bug Reports"
          value={stats?.bugReports ?? 0}
          status={bugStatus}
          Icon={Bug}
        />
        <HealthIndicator
          label="User Satisfaction"
          value={stats?.avgRating ? `${stats.avgRating}/5` : "N/A"}
          status={ratingStatus}
          Icon={TrendingUp}
        />
        <HealthIndicator
          label="Actioned Items"
          value={stats?.actioned ?? 0}
          status="good"
          Icon={CheckCircle}
        />
      </div>

      {/* Quick Links */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-neutral-700">
        <Link
          to="/admin/feedback"
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition"
        >
          <MessageSquareText className="w-3.5 h-3.5" />
          Feedback
        </Link>
        <Link
          to="/admin/tickets"
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition"
        >
          <TicketCheck className="w-3.5 h-3.5" />
          Tickets
        </Link>
        <Link
          to="/admin/popups"
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition"
        >
          <Activity className="w-3.5 h-3.5" />
          Popups
        </Link>
      </div>
    </div>
  );
}
